import _ from 'lodash';
import { $merge, $set, update } from 'qim';

import { createReducer } from '@zapier/toolbox-redux-framework/src/redux-utils';
import { ensureArray } from '@zapier/common-utils';

import * as statuses from './utils/statuses';
import EntityActionTypes from './EntityActionTypes';
import {
  createCollection,
  createEntity,
  generateEntityCid,
} from './utils/entityUtils';

// Deeply merges the passed `collections` into `state`, allowing
// any deep structure / value within a collection to be specified,
// like if you want to specify the state for a collection or data/state
// for an entity within a collection for instance.
const deepMergeCollections = (collections, state) =>
  _.defaultsDeep({}, collections, state);

// Collection utilities

const resetCollection = (entityType, state) =>
  update([entityType, $set(createCollection())], state);

const updateCollection = (key, entityType, delta, state) =>
  update([entityType, key, $merge(delta)], state);

const updateCollectionEntities = _.partial(updateCollection, 'entities');

const resetCollectionEntities = (entityType, state) =>
  update([entityType, 'entities', $set({})], state);

const createCollectionEntity = (entityType, data, entityState, state) =>
  updateCollectionEntities(
    entityType,
    {
      [data.id]: createEntity(data.id, data, entityState),
    },
    state
  );

const deleteCollectionEntity = (entityType, id, state) =>
  updateCollectionEntities(entityType, { [id]: null }, state);

const updateCollectionData = _.partial(updateCollection, 'data');

const updateCollectionState = _.partial(updateCollection, 'state');

const updateCollectionStatus = (entityType, status, statusMessages, state) =>
  updateCollectionState(
    entityType,
    {
      status,
      statusMessages: ensureArray(statusMessages).filter(Boolean),
    },
    state
  );

// Entity utilities

const updateEntity = (key, entityType, id, delta, state) =>
  update(
    [
      entityType,
      'entities',
      id,
      key,
      _.isObject(delta) ? $merge(delta) : $set(delta),
    ],
    state
  );

const updateEntityCid = _.partial(updateEntity, 'cid');

const updateEntityData = _.partial(updateEntity, 'data');

const updateEntityState = _.partial(updateEntity, 'state');

const updateEntityStatus = (
  entityType,
  id,
  status,
  statusMessages,
  statusCode,
  state
) =>
  updateEntityState(
    entityType,
    id,
    {
      status,
      statusCode,
      statusMessages: ensureArray(statusMessages).filter(Boolean),
    },
    state
  );

const fetchCollection = (state, action) => {
  const { entityType, messages, status } = action;
  return updateCollectionStatus(entityType, status, messages, state);
};

const fetchCollectionDone = (state, action) => {
  const { entityType, collection, messages } = action;
  // v3 apis return `objects` key, v4 return `results` so
  // we need to handle both cases
  const entitiesKey = 'objects';
  const entitiesAltKey = 'results';
  const entities =
    _.get(collection, entitiesKey) || _.get(collection, entitiesAltKey, []);
  const metadata = _.omit(collection, [entitiesKey, entitiesAltKey]);
  return _.flow(
    _.partial(updateCollectionState, entityType, {}),
    _.partial(updateCollectionStatus, entityType, statuses.SUCCESS, messages),
    _.partial(updateCollectionData, entityType, metadata),
    // Map into entity-creating functions that receive the
    // new state for each entity.
    ...entities.map(entity =>
      _.partial(createCollectionEntity, entityType, entity, null)
    )
  )(state);
};

const fetchCollectionFail = (state, action) => {
  const { entityType, messages } = action;
  return updateCollectionStatus(entityType, statuses.FAILURE, messages, state);
};

// Reducer

const handlers = {
  [EntityActionTypes.INITIALIZE_COLLECTIONS](state, action) {
    return deepMergeCollections(action.collections, state);
  },

  [EntityActionTypes.RESET_COLLECTION](state, action) {
    const { entityType } = action;
    return resetCollection(entityType, state);
  },

  [EntityActionTypes.RESET_COLLECTION_ENTITIES](state, action) {
    const { entityType } = action;
    return _.flow(
      _.partial(resetCollectionEntities, entityType),
      _.partial(updateCollectionStatus, entityType, statuses.INITIAL, undefined)
    )(state);
  },

  // Note that if an entity already exists with the same
  // id that it will be _replaced_ and any state or data
  // that it had will be lost. Should we need a create
  // or update (or even throw if exists), we can either
  // create a new action or add it as an option to this one.
  [EntityActionTypes.CREATE_COLLECTION_ENTITY](state, action) {
    const { entityType, data } = action;
    return createCollectionEntity(entityType, data, action.state, state);
  },

  [EntityActionTypes.DELETE_COLLECTION_ENTITIES](state, action) {
    const { entityType, ids } = action;
    return _.flow(
      ...ensureArray(ids).map(id =>
        _.partial(deleteCollectionEntity, entityType, id)
      )
    )(state);
  },

  [EntityActionTypes.UPDATE_COLLECTION_DATA](state, action) {
    const { entityType } = action;
    return updateCollectionData(entityType, action.data, state);
  },

  [EntityActionTypes.UPDATE_COLLECTION_STATE](state, action) {
    const { entityType } = action;
    return updateCollectionState(entityType, action.state, state);
  },

  [EntityActionTypes.FETCH_COLLECTION]: fetchCollection,

  [EntityActionTypes.FETCH_COLLECTION_DONE]: fetchCollectionDone,
  [EntityActionTypes.SILENT_FETCH_COLLECTION_DONE](state, action) {
    const { entityType } = action;
    return fetchCollectionDone(resetCollection(entityType, state), action);
  },

  [EntityActionTypes.FETCH_COLLECTION_FAIL]: fetchCollectionFail,
  [EntityActionTypes.SILENT_FETCH_COLLECTION_FAIL]: fetchCollectionFail,

  [EntityActionTypes.UPDATE_ENTITY_DATA](state, action) {
    const { entityType, data, id } = action;
    return updateEntityData(entityType, id, data, state);
  },

  [EntityActionTypes.UPDATE_ENTITY_STATE](state, action) {
    const { entityType, id } = action;
    return updateEntityState(entityType, id, action.state, state);
  },

  [EntityActionTypes.FETCH_ENTITY](state, action) {
    const { entityType, id, messages, status, statusCode } = action;
    return updateEntityStatus(
      entityType,
      id,
      status,
      messages,
      statusCode,
      state
    );
  },

  [EntityActionTypes.FETCH_ENTITY_DONE](state, action) {
    const { entityType, entity, id, messages, status, statusCode } = action;
    const flow = [
      // Always update the status.
      _.partial(
        updateEntityStatus,
        entityType,
        id,
        status,
        messages,
        statusCode
      ),
    ];
    // Entity has been deleted, so remove it from state.
    if (entity == null) {
      flow.push(_.partial(deleteCollectionEntity, entityType, id));
      // Entity has been created. `entity.id` is the persisted
      // id and `id` was a client id. Remove the client version
      // of the entity and replace it with the server version,
      // keeping the client version's state intact.
    } else if (entity.id !== id) {
      // Note that all the logic here is encapsulated into a function
      // named `cloneEntityState` because we want the `transitioningState`
      // to reflect any prior transformations being piped through `_.flow`.
      // Specifically `updateEntityStatus` is called and we want to have
      // access to the changes resulting from that function call here.
      const cloneEntityState = transitioningState => {
        // It would be nice to use selectors here but it's semi-complicated
        // because `state` is the state of _just the entities reducer_
        // whereas selectors expect the state of the _whole app_. So it's
        // either manually grab this path or wrap `state` with a key.
        const oldEntity = transitioningState[entityType].entities[id];
        const newEntityState = { ..._.omit(oldEntity.state, 'isPersisted') };
        return _.flow(
          // Remove the old client entity.
          _.partial(deleteCollectionEntity, entityType, id),
          // Create the new entity to replace the client entity,
          // preserving the client entity's state.
          _.partial(createCollectionEntity, entityType, entity, newEntityState),
          // Reassign the old client entity's `cid` to the new entity
          // so that it's clear that the new entity is based off of
          // the old client entity. This allows the new entity to be
          // looked up by the original client entity's `cid`.
          _.partial(
            updateEntityCid,
            entityType,
            String(entity.id),
            oldEntity.cid || generateEntityCid()
          )
        )(transitioningState);
      };
      flow.push(cloneEntityState);
      // Entity has been updated. A nice, simple update.
    } else {
      flow.push(_.partial(updateEntityData, entityType, id, entity));
    }
    return _.flow(...flow)(state);
  },

  [EntityActionTypes.FETCH_ENTITY_FAIL](state, action) {
    const { entityType, id, messages, status, statusCode } = action;
    return updateEntityStatus(
      entityType,
      id,
      status,
      messages,
      statusCode,
      state
    );
  },

  [EntityActionTypes.RESET_ENTITY_STATUS](state, action) {
    const { entityType, id } = action;
    return updateEntityStatus(
      entityType,
      id,
      statuses.INITIAL,
      [],
      undefined,
      state
    );
  },
};

const initialState = {};

export default createReducer(handlers, initialState);
